/**
 * Licensed Materials - Property of HCL Technologies Limited.
 * (C) Copyright HCL Technologies Limited  2023.
 */

import { Status } from '@/components/content/Register';
import { SxProps, Theme } from '@mui/material';
import { OpenSansFontWeights } from 'shared/custom/constants/commonConstants';

export const registerInputSx: SxProps<Theme> = {
	'& .MuiInputBase-input': {
		fontSize: '12px',
		p: '10px',
	},
	'& .MuiInputLabel-root': {
		top: -6,
		color: 'grey.200',
		fontSize: '15px',
		mb: 0,
	},
	'& .MuiInputLabel-shrink': {
		top: 0,
		color: 'darkgray !important',
		fontSize: '14px',
	},
	'& .MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline': {
		borderColor: 'primary.main',
	},
	'& .MuiOutlinedInput-root.Mui-error .MuiOutlinedInput-notchedOutline': {
		borderColor: '#0000003b',
		borderBottomColor: 'error.main',
		color: 'black',
	},
};

type RegisterSxType = {
	container: SxProps<Theme>;
	[key: string]: SxProps<Theme>;
};

export const RegisterSx: RegisterSxType = {
	container: {
		px: { sm: '10px', lg: '20px' },
		pb: { sm: 3, md: 0 },
	},
	formContainer: {
		flexDirection: { sm: 'column', md: 'row' },
		width: 'calc(100% - 20px)',
	},
	title: {
		alignSelf: 'start',
		fontWeight: OpenSansFontWeights.light,
		color: 'grey.500',
		width: 'fit-content',
		borderBottom: '2px solid',
		borderBottomColor: 'primary.main',
		marginBottom: '5px',
		fontSize: '27px',
		whiteSpace: 'break-spaces',
		'-webkit-box-align': 'baseline',
		alignItems: 'baseline',
	},
	titleDivider: {
		flexGrow: '1',
		marginBottom: '5px',
		borderColor: 'grey.50',
	},
	link: {
		color: 'secondary.main',
		textDecoration: 'none',
		'&:hover': {
			color: 'secondary.main',
			textDecoration: 'none',
		},
	},
	text: {
		color: 'grey.500',
		mt: '10px',
	},
	button: {
		p: '10px',
		mb: '10px',
	},
	checkboxLabel: {
		'& .MuiFormControlLabel-label': {
			fontWeight: '400',
			color: 'grey.500',
			fontSize: '12px',
		},
	},
	termsConditionsText: {
		color: 'grey.500',
		pb: '10px',
	},
	termsConditionsLink: {
		color: 'secondary.main',
		'&:hover': {
			textDecoration: 'underline',
		},
	},
	input: {
		mt: '10px',
		...registerInputSx,
	},
	passwordRequirement: {
		listStyle: 'none',
		p: 0,
		'& li': {
			display: 'flex',
			alignItems: 'center',
			maxHeight: { sm: '35px', md: 'none' },
		},
		'& li:before': {
			content: '"•"',
			pr: '5px',
			fontWeight: '900',
			fontSize: '28px',
		},
	},
	gridItem: {
		p: '2.5px',
	},
	helperText: { ml: '0', fontSize: '10px' },
	gridContainerItem: {
		mt: '0 !important',
	},
	validationPasswordTitleContainer: {
		p: '2.5px',
	},
	validationPasswordTitleText: {
		mt: '10px',
	},
	passwordInput: {
		mb: '0',
		my: '10px',
		...registerInputSx,
	},
	imageContainer: {
		display: 'flex',
		justifyContent: 'center',
		alignItems: 'center',
	},
	resendReminder: {
		textAlign: 'center',
	},
};

export const formSx = (isDialogView = false) => ({
	width: !isDialogView ? { sm: '100%', md: '50%' } : '100%',
	p: '20px',
	borderRadius: '0',
	...(isDialogView
		? {
				maxWidth: '610px',
		  }
		: null),
});

export const passwordRequirementSX = (status = Status.Neutral) => ({
	color:
		status === Status.Success
			? 'success.main'
			: status === Status.Error
			? 'error.main'
			: 'grey.300',
	'&:before': {
		color:
			status === Status.Success
				? 'success.main'
				: status === Status.Error
				? 'error.main'
				: 'grey.300',
	},
});

export const registerContainerSx = (isDialogView = false) => ({
	px: '20px',
	m: '20px',
	border: '0',
	...(!isDialogView
		? {
				padding: { sm: '0 15px', md: '0 30px' },
				borderRight: { sm: '0', md: '1px solid' },
				borderRightColor: 'backgroundGray',
		  }
		: null),
});
